<script lang="ts">
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import {
    testContactOrderStep,
    type ContactOrderStep,
  } from "../../../../../../../../../api/kehrblatt/contact-orders"
  import type { ContactOrderPlanRow } from "../../../../_logic/communication-plan-table-contract"
  import { isTerminalStepStatus } from "../../_shared/_logic/action-availability"

  let {
    row,
    step,
    stepId,
    caseId,
    contactOrderId,
    allowed,
    onClose,
  }: {
    row: ContactOrderPlanRow
    step: ContactOrderStep | null
    stepId: string
    caseId: string
    contactOrderId: string
    allowed: boolean
    onClose: () => void
  } = $props()

  let showDialog = $state(true)
  let targetValue = $state("")
  let submitting = $state(false)
  let errorMessage = $state<string | null>(null)

  const channel = $derived(String(row.channel || "").toUpperCase())
  const isTestable = $derived(["EMAIL", "MOBILE_MESSAGE", "PHONE"].includes(channel))
  const isTerminalStep = $derived(isTerminalStepStatus(step?.status ?? row.status))
  const targetLabel = $derived(
    channel === "EMAIL"
      ? "E-Mail-Adresse"
      : channel === "MOBILE_MESSAGE"
        ? "Mobilnummer"
        : "Telefonnummer",
  )
  const placeholder = $derived(
    channel === "EMAIL"
      ? "test@example.de"
      : channel === "MOBILE_MESSAGE"
        ? "+49 170 1234567"
        : "+49 30 123456",
  )
  const inputMode = $derived(channel === "EMAIL" ? "email" : "tel")

  const validateTarget = () => {
    const value = targetValue.trim()

    if (!value) return `Bitte ${targetLabel} eingeben.`

    if (channel === "EMAIL" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      return "Bitte eine gültige E-Mail-Adresse eingeben."
    }

    if (channel !== "EMAIL") {
      const digits = value.replace(/\D/g, "")

      if (!/^[0-9+()\s./-]+$/.test(value) || digits.length < 5) {
        return `Bitte eine gültige ${targetLabel} eingeben.`
      }
    }

    return null
  }

  const submitTest = async (close: () => void) => {
    if (!stepId || submitting) return

    const validationError = validateTarget()

    if (validationError) {
      errorMessage = validationError

      return
    }

    submitting = true
    errorMessage = null
    try {
      await testContactOrderStep(caseId, contactOrderId, stepId, {
        target_value: targetValue.trim(),
      })
      newNotification({
        class: "success",
        html: "Testausführung wurde gestartet.",
      })
      targetValue = ""
      close()
    } catch (err) {
      console.error("contact_order_step_test_failed", err)
      errorMessage = "Testausführung konnte nicht gestartet werden."
      newNotification({
        class: "error",
        html: "Testausführung konnte nicht gestartet werden.",
      })
    } finally {
      submitting = false
    }
  }
</script>

{#if showDialog}
  <ModalDialog
    appearance="plain"
    title="Kontaktschritt testen"
    size="medium"
    onClose={() => {
      showDialog = false
      onClose()
    }}
  >
    {#snippet content()}
      <div class="test-step-dialog form">
        <p>Geben Sie eine Testadresse ein.</p>
        <Input
          surfaceTone="light"
          brightBackground={true}
          bind:value={targetValue}
          type="text"
          label={targetLabel}
          {placeholder}
          autocomplete="off"
          inputmode={inputMode}
          required={true}
        />
        {#if errorMessage}
          <p class="test-step-dialog__error">{errorMessage}</p>
        {/if}
      </div>
    {/snippet}

    {#snippet footerSnippet({ close })}
      <div class="test-step-dialog__actions">
        <CTA
          type="primaryButton"
          darkMode={false}
          content={submitting ? "Wird gestartet..." : "Jetzt testen"}
          disabled={submitting}
          onClick={() => submitTest(close)}
        />
      </div>
    {/snippet}
  </ModalDialog>
{/if}

<style lang="less">
  .test-step-dialog {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    p {
      margin: 0;
      color: var(--text-invers-100);
      text-wrap: break-word;
    }

    &__error {
      color: var(--error-100) !important;
      font-weight: 700;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.7rem;
      flex-wrap: wrap;
    }
  }
</style>
